import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import useSpeech from "./useSpeech";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import { ImagePlaceholder } from "./ImagePlaceholderNode";

const Icon = ({ path }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {path}
  </svg>
);

const Btn = ({ active, onClick, children, title }) => (
  <button
    type="button"
    className={`pb-tb${active ? " on" : ""}`}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
  >
    {children}
  </button>
);

const STAR = <path d="M12 3l2 4 4 .5-3 3 .8 4L12 12.7 8.2 14.5 9 10.5 6 7.5 10 7z" />;
const INLINE_ACTIONS = [
  { key: "rewrite", label: "Rewrite", icon: STAR },
  { key: "tone", label: "Tone" },
  { key: "grammar", label: "Grammar" },
  { key: "expand", label: "Expand" },
  { key: "shorten", label: "Shorten" },
];
const ACTION_LABEL = {
  ...INLINE_ACTIONS.reduce((m, a) => ({ ...m, [a.key]: a.label }), {}),
  custom: "Your instruction",
};

/**
 * Continuous-document TipTap editor for the playbook — the whole book is one
 * scrollable document (every page is an <h1> section). Exposes its outline and
 * an active-section index (scroll-spy) so the left rail can follow along, plus
 * scrollToHeading() so the rail can jump. `runInline(action, text)` returns
 * replacement HTML for the current selection (calls YETI on the server).
 */
const TipTapEditor = forwardRef(
  ({ content, editable = true, onChange, runInline, onOutline, onActiveHeading, onUploadImage, header }, ref) => {
    const [inlineAsk, setInlineAsk] = useState("");
    const inlineMicBase = useRef("");
    /* Grow the ask box with what is in it — two lines at rest, five at most,
       then it scrolls. Reset to auto first or scrollHeight only ever reports
       the height we last set and the box can grow but never shrink back. */
    const inlineAskRef = useRef(null);
    useEffect(() => {
      const el = inlineAskRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 92)}px`;
    }, [inlineAsk]);
    const inlineMic = useSpeech((t) =>
      setInlineAsk(`${inlineMicBase.current ? `${inlineMicBase.current} ` : ""}${t}`)
    );
    // Highlight-to-edit preview: YETI's suggestion is held here for review and
    // is NOT applied to the document until the user clicks Replace.
    const [preview, setPreview] = useState(null); // { action, from, to, original, proposed, loading }
    const [imgBusy, setImgBusy] = useState(false);
    const previewRef = useRef(null);
    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const pickRef = useRef(null); // latest pickAndUpload, for the placeholder node
    const pickResolverRef = useRef(null);

    const editor = useEditor({
      editable,
      extensions: [
        StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
        Underline,
        Highlight,
        Link.configure({ openOnClick: false, autolink: true }),
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Image.configure({ HTMLAttributes: { class: "pb-img" } }),
        ImagePlaceholder.configure({
          onFill: () => (pickRef.current ? pickRef.current() : Promise.resolve(null)),
        }),
        Placeholder.configure({
          placeholder: "Start writing, or ask YETI to draft this handbook…",
        }),
      ],
      content: content || "",
      editorProps: { attributes: { class: "pb-prose" } },
      onUpdate: ({ editor: ed }) => onChange && onChange(ed.getHTML()),
    });

    // Report the outline (every top-level <h1> title, in order).
    const recomputeOutline = useCallback(() => {
      if (!editor || !onOutline) return;
      const h1s = editor.view.dom.querySelectorAll("h1");
      onOutline(Array.from(h1s).map((el, i) => (el.textContent || "").trim() || `Section ${i + 1}`));
    }, [editor, onOutline]);

    // Keep editor content in sync when it changes from outside (YETI generate,
    // page load). Guard against clobbering the user's typing.
    useEffect(() => {
      if (!editor) return;
      const incoming = content || "";
      if (incoming !== editor.getHTML()) {
        editor.commands.setContent(incoming, false);
      }
      recomputeOutline();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, editor]);

    useEffect(() => {
      if (editor) editor.setEditable(editable);
    }, [editable, editor]);

    // Mirror preview into a ref so the BubbleMenu's shouldShow (called outside
    // React render) can keep the popover open while a suggestion is under review.
    useEffect(() => {
      previewRef.current = preview;
    }, [preview]);

    useEffect(() => {
      if (!editor) return undefined;
      const handler = () => recomputeOutline();
      editor.on("update", handler);
      return () => editor.off("update", handler);
    }, [editor, recomputeOutline]);

    // Scroll-spy: which <h1> is currently at the top of the scroll area.
    useEffect(() => {
      const root = scrollRef.current;
      if (!root || !editor || !onActiveHeading) return undefined;
      const onScroll = () => {
        const h1s = editor.view.dom.querySelectorAll("h1");
        const rootTop = root.getBoundingClientRect().top;
        let idx = 0;
        h1s.forEach((el, i) => {
          if (el.getBoundingClientRect().top - rootTop <= 110) idx = i;
        });
        onActiveHeading(idx);
      };
      root.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
      return () => root.removeEventListener("scroll", onScroll);
    }, [editor, onActiveHeading, content]);

    useImperativeHandle(
      ref,
      () => ({
        scrollToHeading(i) {
          const root = scrollRef.current;
          if (!root || !editor) return;
          const target = editor.view.dom.querySelectorAll("h1")[i];
          if (!target) return;
          const top =
            root.scrollTop + (target.getBoundingClientRect().top - root.getBoundingClientRect().top) - 12;
          root.scrollTo({ top, behavior: "smooth" });
        },
      }),
      [editor]
    );

    // Ask YETI for a suggestion and hold it for review (does not touch the doc).
    const runAction = async (action, from, to, original, opts = {}) => {
      setPreview({ action, from, to, original, opts, proposed: "", loading: true });
      try {
        const replacement = await runInline(action, original, opts);
        setPreview((p) =>
          p && p.from === from && p.to === to
            ? { ...p, proposed: replacement || "", loading: false }
            : p
        );
      } catch (e) {
        setPreview(null);
      }
    };

    // The paragraph around the selection travels along so YETI matches the
    // page's voice instead of rewriting in a vacuum.
    const contextAround = (from, to) => {
      const { size } = editor.state.doc.content;
      return editor.state.doc.textBetween(Math.max(0, from - 400), Math.min(size, to + 400), " ");
    };

    const handleInline = (action, instruction) => {
      if (!runInline || !editor) return;
      const { from, to } = editor.state.selection;
      if (from === to) return;
      const original = editor.state.doc.textBetween(from, to, " ");
      if (!original.trim()) return;
      runAction(action, from, to, original, {
        instruction: instruction || undefined,
        context: contextAround(from, to),
      });
    };

    const submitAsk = () => {
      const instruction = inlineAsk.trim();
      if (!instruction) return;
      inlineMic.stop();
      setInlineAsk("");
      handleInline("custom", instruction);
    };

    // Apply the reviewed suggestion to the document.
    const applyPreview = () => {
      if (!editor || !preview || !preview.proposed) return;
      editor
        .chain()
        .focus()
        .deleteRange({ from: preview.from, to: preview.to })
        .insertContent(preview.proposed)
        .run();
      setPreview(null);
    };
    const retryPreview = () => {
      if (!preview) return;
      runAction(preview.action, preview.from, preview.to, preview.original, preview.opts || {});
    };
    const discardPreview = () => setPreview(null);

    // Promise-based picker shared by the toolbar button and the placeholder
    // cards. Resolves with the uploaded image URL (or null).
    const pickAndUpload = () =>
      new Promise((resolve) => {
        pickResolverRef.current = resolve;
        if (fileInputRef.current) fileInputRef.current.click();
        else resolve(null);
      });
    pickRef.current = pickAndUpload;

    const onFilePicked = async (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      const resolve = pickResolverRef.current;
      pickResolverRef.current = null;
      if (!file || !onUploadImage) {
        if (resolve) resolve(null);
        return;
      }
      setImgBusy(true);
      try {
        const src = await onUploadImage(file);
        if (resolve) resolve(src || null);
      } catch (err) {
        if (resolve) resolve(null);
      } finally {
        setImgBusy(false);
      }
    };

    const pickImage = async () => {
      const src = await pickAndUpload();
      if (src && editor) editor.chain().focus().setImage({ src }).run();
    };

    const setLink = () => {
      if (!editor) return;
      const prev = editor.getAttributes("link").href;
      // eslint-disable-next-line no-alert
      const url = window.prompt("Link URL", prev || "https://");
      if (url === null) return;
      if (url === "") {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    if (!editor) return null;

    return (
      <div className="pb-editor">
        {editable && (
          <div className="pb-toolbar">
            <Btn title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></Btn>
            <Btn title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><span style={{ fontStyle: "italic" }}>I</span></Btn>
            <Btn title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><span style={{ textDecoration: "underline" }}>U</span></Btn>
            <Btn title="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><span style={{ textDecoration: "line-through" }}>S</span></Btn>
            <Btn title="Highlight" active={editor.isActive("highlight")} onClick={() => editor.chain().focus().toggleHighlight().run()}>
              <Icon path={<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V4s-1 1-4 1-5-2-8-2-4 1-4 1z" />} />
            </Btn>
            <span className="pb-tb-div" />
            <Btn title="Heading 1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</Btn>
            <Btn title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</Btn>
            <Btn title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</Btn>
            <span className="pb-tb-div" />
            <Btn title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
              <Icon path={<><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></>} />
            </Btn>
            <Btn title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
              <Icon path={<><path d="M10 6h11M10 12h11M10 18h11" /><path d="M4 6h1v4M4 10h2M6 18H4l2-3H4" /></>} />
            </Btn>
            <Btn title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
              <Icon path={<path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />} />
            </Btn>
            <Btn title="Code block" active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
              <Icon path={<><path d="m9 18-6-6 6-6" /><path d="m15 6 6 6-6 6" /></>} />
            </Btn>
            <span className="pb-tb-div" />
            <Btn title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}>
              <Icon path={<><path d="M4 6h16" /><path d="M4 12h10" /><path d="M4 18h13" /></>} />
            </Btn>
            <Btn title="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}>
              <Icon path={<><path d="M4 6h16" /><path d="M7 12h10" /><path d="M6 18h12" /></>} />
            </Btn>
            <Btn title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}>
              <Icon path={<><path d="M4 6h16" /><path d="M10 12h10" /><path d="M7 18h13" /></>} />
            </Btn>
            <span className="pb-tb-div" />
            <Btn title="Link" active={editor.isActive("link")} onClick={setLink}>
              <Icon path={<><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></>} />
            </Btn>
            {onUploadImage && (
              <Btn title="Insert image" onClick={pickImage}>
                {imgBusy ? (
                  <span style={{ fontSize: 11 }}>…</span>
                ) : (
                  <Icon path={<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>} />
                )}
              </Btn>
            )}
            <Btn title="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
              <Icon path={<path d="M5 12h14" />} />
            </Btn>
            <span className="pb-tb-div" />
            <Btn title="Undo" onClick={() => editor.chain().focus().undo().run()}>
              <Icon path={<><path d="M9 14 4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-4" /></>} />
            </Btn>
            <Btn title="Redo" onClick={() => editor.chain().focus().redo().run()}>
              <Icon path={<><path d="m15 14 5-5-5-5" /><path d="M20 9H9a5 5 0 0 0 0 10h4" /></>} />
            </Btn>
          </div>
        )}

        {editable && runInline && (
          <BubbleMenu
            editor={editor}
            tippyOptions={{ duration: 120, maxWidth: "none", placement: "bottom" }}
            shouldShow={({ from, to }) => to > from || !!previewRef.current}
          >
            {!preview ? (
              <div className="pb-inline-wrap" onMouseDown={(e) => e.preventDefault()}>
                <div className="pb-inline-pop">
                  {INLINE_ACTIONS.map((a) => (
                    <button key={a.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleInline(a.key)}>
                      {a.icon && <Icon path={a.icon} />}
                      {a.label}
                    </button>
                  ))}
                </div>
                <div className="pb-inline-ask">
                  {/* A textarea, not an input. As an <input> this could only ever
                      be one line — you typed past the edge and had to scroll
                      sideways to read your own sentence. It opens at two lines
                      and grows to five, then scrolls. Enter still sends;
                      Shift+Enter breaks the line, which a multi-line box needs
                      or you can never write the second one. */}
                  <textarea
                    ref={inlineAskRef}
                    rows={2}
                    value={inlineAsk}
                    placeholder="Tell YETI how to change it… (e.g. turn into bullet points)"
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => setInlineAsk(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        submitAsk();
                      }
                    }}
                  />
                  {inlineMic.supported && (
                    <button
                      type="button"
                      className={`pb-mic${inlineMic.listening ? " on" : ""}`}
                      title={inlineMic.listening ? "Stop dictating" : "Dictate"}
                      onClick={() => {
                        inlineMicBase.current = inlineAsk.trim();
                        inlineMic.toggle();
                      }}
                    >
                      <Icon path={<><path d="M12 3a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3Z" /><path d="M19 11a7 7 0 0 1-14 0" /><path d="M12 18v3" /></>} size={13} />
                    </button>
                  )}
                  <button type="button" className="pb-inline-go" disabled={!inlineAsk.trim()} onClick={submitAsk}>
                    <Icon path={<path d="m5 12 14-7-7 14-2-5z" />} size={13} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="pb-inline-preview" onMouseDown={(e) => e.preventDefault()}>
                <div className="pb-ip-head">
                  <span className="pb-ip-ic"><Icon path={STAR} /></span>
                  <span className="pb-ip-title">{ACTION_LABEL[preview.action] || "Suggestion"}</span>
                  <span className="pb-ip-tag">YETI</span>
                </div>
                {preview.loading && <div className="pb-ip-loading">Writing…</div>}
                {!preview.loading &&
                  preview.proposed.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() ===
                    preview.original.replace(/\s+/g, " ").trim() && (
                    <div className="pb-ip-clean">
                      Already clean — nothing to fix here.
                    </div>
                  )}
                {!preview.loading &&
                  preview.proposed.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() !==
                    preview.original.replace(/\s+/g, " ").trim() && (
                  <div className="pb-ip-body">
                    <div className="pb-ip-lbl">Your text</div>
                    <div className="pb-ip-was">{preview.original}</div>
                    <div className="pb-ip-lbl">Proposed</div>
                    {/* eslint-disable-next-line react/no-danger */}
                    <div className="pb-ip-now" dangerouslySetInnerHTML={{ __html: preview.proposed }} />
                  </div>
                )}
                <div className="pb-ip-acts">
                  <button className="pb-ip-btn primary" disabled={preview.loading || !preview.proposed} onClick={applyPreview}>
                    <Icon path={<path d="M20 6 9 17l-5-5" />} /> Replace
                  </button>
                  <button className="pb-ip-btn ghost" disabled={preview.loading} onClick={retryPreview}>
                    <Icon path={<><path d="M3 12a9 9 0 1 0 9-9 9 9 0 0 0-6.5 2.7L3 8" /><path d="M3 3v5h5" /></>} /> Try again
                  </button>
                  <span className="pb-ip-spacer" />
                  <button className="pb-ip-btn link" onClick={discardPreview}>Discard</button>
                </div>
              </div>
            )}
          </BubbleMenu>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={onFilePicked}
        />

        <div className="pb-editor-scroll" ref={scrollRef}>
          {header}
          <div className="pb-doc">
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>
    );
  }
);

export default TipTapEditor;
